import { useState } from "react";
import { tiposIndividuoOptions, classesRiscoOptions, reviewStatusLabel } from "@/config/cta-mapa-config";

/** Opcoes derivadas do cadastro real carregado no mapa. */
export interface MapFilterOptions {
  projetos: string[];
  bairros: string[];
  especies: string[];
  statusRevisao: string[];
  /** Tecnicos que registraram vistoria em algum exemplar do mapa. */
  tecnicos: string[];
}

interface MapFilterPanelProps {
  collapsed: boolean;
  onToggle: () => void;
  options: MapFilterOptions;
  onApplyFilters: (filters: Record<string, string>) => void;
  onClearFilters: () => void;
}

export default function MapFilterPanel({ collapsed, onToggle, options, onApplyFilters, onClearFilters }: MapFilterPanelProps) {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    projeto: true,
    localizacao: false,
    individuo: false,
    especie: false,
    risco: true,
    revisao: false,
    tecnico: false,
  });

  const toggleSection = (key: string) => setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const updateFilter = (key: string, value: string) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (value) next[key] = value;
      else delete next[key];
      return next;
    });
  };

  const handleApply = () => onApplyFilters(filters);
  const handleClear = () => {
    setFilters({});
    onClearFilters();
  };

  const selectClass =
    "w-full text-xs border border-background-200 rounded-md px-2.5 py-2 bg-white text-foreground-700 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-300 transition-colors";

  if (collapsed) {
    return (
      <div className="w-10 bg-white border-r border-background-200/70 flex flex-col items-center py-3 gap-3 shrink-0">
        <button onClick={onToggle} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors" title="Expandir filtros">
          <i className="ri-arrow-right-s-line text-foreground-500 text-lg"></i>
        </button>
        <button onClick={handleApply} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-primary-50 text-primary-500 transition-colors" title="Aplicar filtros">
          <i className="ri-filter-3-line text-base"></i>
        </button>
        <button onClick={handleClear} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 transition-colors" title="Limpar filtros">
          <i className="ri-close-line text-base"></i>
        </button>
      </div>
    );
  }

  const SectionHeader = ({ keyName, label, icon }: { keyName: string; label: string; icon: string }) => (
    <button
      onClick={() => toggleSection(keyName)}
      className="w-full flex items-center justify-between gap-2 py-2 text-xs font-semibold text-foreground-700 font-heading hover:text-foreground-900 transition-colors"
    >
      <span className="flex items-center gap-1.5 whitespace-nowrap">
        <i className={`${icon} text-xs`} aria-hidden="true"></i>
        {label}
      </span>
      <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${expandedSections[keyName] ? "rotate-180" : ""}`}></i>
    </button>
  );

  const emptyHint = (text: string) => <p className="text-[10px] text-foreground-400 pb-1">{text}</p>;

  return (
    <aside className="w-[260px] bg-white border-r border-background-200/70 flex flex-col shrink-0 h-full">
      <div className="flex items-center justify-between px-3 py-3 border-b border-background-200/70">
        <div className="flex items-center gap-2">
          <i className="ri-filter-3-line text-accent-500 text-base" aria-hidden="true"></i>
          <span className="text-sm font-semibold text-foreground-900 font-heading whitespace-nowrap">Filtros</span>
        </div>
        <button onClick={onToggle} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors" title="Recolher filtros">
          <i className="ri-arrow-left-s-line text-foreground-500 text-base"></i>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
        <div>
          <SectionHeader keyName="projeto" label="Projeto" icon="ri-folder-chart-line" />
          {expandedSections.projeto &&
            (options.projetos.length === 0 ? (
              emptyHint("Nenhum projeto com exemplares no mapa.")
            ) : (
              <select value={filters.projeto || ""} onChange={(e) => updateFilter("projeto", e.target.value)} className={selectClass}>
                <option value="">Todos os projetos</option>
                {options.projetos.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            ))}
        </div>

        <div>
          <SectionHeader keyName="localizacao" label="Localização" icon="ri-map-pin-line" />
          {expandedSections.localizacao && (
            <div className="space-y-2 pt-1">
              {options.bairros.length > 0 && (
                <select value={filters.bairro || ""} onChange={(e) => updateFilter("bairro", e.target.value)} className={selectClass}>
                  <option value="">Todos os bairros</option>
                  {options.bairros.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              )}
              <input
                type="text"
                placeholder="Logradouro..."
                value={filters.logradouro || ""}
                onChange={(e) => updateFilter("logradouro", e.target.value)}
                className={selectClass}
              />
            </div>
          )}
        </div>

        <div>
          <SectionHeader keyName="individuo" label="Tipo de Indivíduo" icon="ri-plant-line" />
          {expandedSections.individuo && (
            <select value={filters.tipo || ""} onChange={(e) => updateFilter("tipo", e.target.value)} className={selectClass}>
              <option value="">Todos os tipos</option>
              {tiposIndividuoOptions.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          )}
        </div>

        <div>
          <SectionHeader keyName="especie" label="Espécie" icon="ri-leaf-line" />
          {expandedSections.especie &&
            (options.especies.length === 0 ? (
              emptyHint("Nenhuma espécie identificada nos exemplares do mapa.")
            ) : (
              <select value={filters.especie || ""} onChange={(e) => updateFilter("especie", e.target.value)} className={selectClass}>
                <option value="">Todas as espécies</option>
                {options.especies.map((e) => (
                  <option key={e} value={e}>{e}</option>
                ))}
              </select>
            ))}
        </div>

        <div>
          <SectionHeader keyName="risco" label="Risco (última avaliação)" icon="ri-alert-line" />
          {expandedSections.risco && (
            <select value={filters.classeRisco || ""} onChange={(e) => updateFilter("classeRisco", e.target.value)} className={selectClass}>
              <option value="">Todas as classes</option>
              {classesRiscoOptions.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
              <option value="SEM_AVALIACAO">Sem avaliação de risco</option>
            </select>
          )}
        </div>

        <div>
          <SectionHeader keyName="revisao" label="Status de Revisão" icon="ri-check-double-line" />
          {expandedSections.revisao &&
            (options.statusRevisao.length === 0 ? (
              emptyHint("Sem status de revisão nos exemplares do mapa.")
            ) : (
              <select value={filters.statusRevisao || ""} onChange={(e) => updateFilter("statusRevisao", e.target.value)} className={selectClass}>
                <option value="">Todos os status</option>
                {options.statusRevisao.map((s) => (
                  <option key={s} value={s}>{reviewStatusLabel(s)}</option>
                ))}
              </select>
            ))}
        </div>

        <div>
          <SectionHeader keyName="tecnico" label="Técnico da vistoria" icon="ri-user-line" />
          {expandedSections.tecnico &&
            (options.tecnicos.length === 0 ? (
              emptyHint("Nenhuma vistoria registrada nos exemplares do mapa.")
            ) : (
              <select value={filters.tecnico || ""} onChange={(e) => updateFilter("tecnico", e.target.value)} className={selectClass}>
                <option value="">Todos os técnicos</option>
                {options.tecnicos.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            ))}
        </div>
      </div>

      <div className="border-t border-background-200/70 p-3 space-y-2">
        <button onClick={handleApply} className="w-full btn-primary text-xs py-2 whitespace-nowrap">
          <i className="ri-filter-3-line text-sm" aria-hidden="true"></i>
          Aplicar filtros
        </button>
        <button onClick={handleClear} className="w-full btn-outline text-xs py-2 whitespace-nowrap">
          <i className="ri-close-line text-sm" aria-hidden="true"></i>
          Limpar filtros
        </button>
      </div>
    </aside>
  );
}
